import { useEffect, useMemo, useState } from "react";
import {
  assignAiHandoff,
  getAiSession,
  listAiSessions,
  resolveAiHandoff,
} from "../../api/adminAiAssistant.js";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const statusClass = (value) => {
  const status = String(value || "").toLowerCase();
  if (status.includes("handoff")) return "badge bg-warning text-dark";
  if (status.includes("complete") || status.includes("confirmed")) return "badge bg-success";
  if (status.includes("failed") || status.includes("cancel")) return "badge bg-danger";
  return "badge bg-secondary";
};

export default function AdminAiConversations({ token }) {
  const [sessions, setSessions] = useState([]);
  const [selectedUuid, setSelectedUuid] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [filterHandoff, setFilterHandoff] = useState(false);

  const selected = useMemo(
    () => sessions.find((item) => item.sessionUuid === selectedUuid) || null,
    [sessions, selectedUuid]
  );

  const loadSessions = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await listAiSessions(token, {
        handoff: filterHandoff ? "true" : "",
        limit: 50,
      });
      const rows = data.data || [];
      setSessions(rows);
      if (!selectedUuid && rows[0]) setSelectedUuid(rows[0].sessionUuid);
    } catch (err) {
      setError(err.message || "Unable to load AI conversations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filterHandoff]);

  useEffect(() => {
    if (!token || !selectedUuid) return;
    let cancelled = false;
    const load = async () => {
      setDetailLoading(true);
      setError("");
      try {
        const data = await getAiSession(token, selectedUuid);
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to load conversation");
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token, selectedUuid]);

  const assign = async () => {
    if (!selectedUuid) return;
    await assignAiHandoff(token, selectedUuid);
    await loadSessions();
    const data = await getAiSession(token, selectedUuid);
    setDetail(data);
  };

  const resolve = async () => {
    if (!selectedUuid) return;
    await resolveAiHandoff(token, selectedUuid, { resolution: "Resolved from admin AI conversations" });
    await loadSessions();
    const data = await getAiSession(token, selectedUuid);
    setDetail(data);
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <p className="text-uppercase text-muted fw-semibold small mb-1">AI Assistant</p>
          <h1 className="h3 mb-0">AI Conversations</h1>
        </div>
        <div className="d-flex align-items-center gap-2">
          <label className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              checked={filterHandoff}
              onChange={(event) => setFilterHandoff(event.target.checked)}
            />
            <span className="form-check-label small">Handoff only</span>
          </label>
          <button className="btn btn-outline-secondary btn-sm" type="button" onClick={loadSessions}>
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <div className="card">
            <div className="card-header bg-white fw-semibold">Sessions</div>
            <div className="list-group list-group-flush" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {loading ? <div className="p-3 text-muted small">Loading...</div> : null}
              {!loading && sessions.length === 0 ? <div className="p-3 text-muted small">No AI conversations yet.</div> : null}
              {sessions.map((item) => (
                <button
                  key={item.sessionUuid}
                  type="button"
                  className={`list-group-item list-group-item-action ${item.sessionUuid === selectedUuid ? "active" : ""}`}
                  onClick={() => setSelectedUuid(item.sessionUuid)}
                >
                  <div className="d-flex justify-content-between gap-2">
                    <strong>{item.destination || item.area || "New enquiry"}</strong>
                    <span className={statusClass(item.currentStage)}>{item.currentStage}</span>
                  </div>
                  <div className="small mt-1">
                    {item.checkIn || "-"} to {item.checkOut || "-"} · {item.adults || 0} adults
                  </div>
                  <div className="small opacity-75 mt-1">{formatDate(item.updatedAt)}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-8">
          <div className="card">
            <div className="card-header bg-white d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <div className="fw-semibold">{selected?.sessionUuid || "Select a session"}</div>
                <div className="small text-muted">
                  Booking: {selected?.bookingReference || selected?.bookingId || "-"} · Payment: {selected?.paymentId || "-"}
                </div>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-outline-primary btn-sm" type="button" disabled={!selectedUuid} onClick={assign}>
                  Assign to me
                </button>
                <button className="btn btn-outline-success btn-sm" type="button" disabled={!selectedUuid} onClick={resolve}>
                  Resolve handoff
                </button>
              </div>
            </div>
            <div className="card-body" style={{ minHeight: 420 }}>
              {detailLoading ? <div className="text-muted small">Loading conversation...</div> : null}
              {detail ? (
                <div className="row g-3">
                  <div className="col-12 col-xl-7">
                    <h2 className="h6">Messages</h2>
                    <div className="border rounded p-3" style={{ maxHeight: 520, overflowY: "auto" }}>
                      {(detail.messages || []).map((message) => (
                        <div key={message.id} className="mb-3">
                          <div className="small text-muted">
                            {message.sender_type} · {message.message_type} · {formatDate(message.created_at)}
                          </div>
                          <div className="fw-medium">{message.content || "-"}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="col-12 col-xl-5">
                    <h2 className="h6">Tool Calls</h2>
                    <div className="border rounded p-3" style={{ maxHeight: 250, overflowY: "auto" }}>
                      {(detail.toolCalls || []).map((tool) => (
                        <div key={tool.id} className="mb-3">
                          <div className="d-flex justify-content-between">
                            <strong>{tool.tool_name}</strong>
                            <span className={statusClass(tool.status)}>{tool.status}</span>
                          </div>
                          <div className="small text-muted">{tool.duration_ms || 0} ms · {formatDate(tool.created_at)}</div>
                        </div>
                      ))}
                    </div>
                    <h2 className="h6 mt-4">Handoffs</h2>
                    <div className="border rounded p-3" style={{ maxHeight: 220, overflowY: "auto" }}>
                      {(detail.handoffs || []).map((handoff) => (
                        <div key={handoff.id} className="mb-3">
                          <div className="d-flex justify-content-between">
                            <strong>{handoff.reason || "Handoff"}</strong>
                            <span className={statusClass(handoff.status)}>{handoff.status}</span>
                          </div>
                          <div className="small text-muted">{formatDate(handoff.requested_at)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-muted small">Select an AI session to view the timeline.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
